import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FileText, Recycle, Clock, DollarSign, CheckCircle, ArrowRight, HardHat, Building2, ChevronDown, AlertTriangle, Scale, Trash2, MessageSquare } from "lucide-react";
import ContactSupportModal from "../components/ContactSupportModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// California Flag component
const CaliforniaFlag = ({ className = "w-8 h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_green-builder-3/artifacts/dnderd21_Cali%20FLAG.png" 
    alt="California" 
    className={`${className} object-contain`}
  />
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [pricing, setPricing] = useState({
    cwmp_price: 59,
    final_report_price: 49,
    bundle_price: 89,
    subscription_price: 99
  });

  // Fetch pricing from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API}/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing(data);
        }
      } catch (error) {
        console.log('Using default pricing');
      }
    };
    fetchPricing();
  }, []);

  const features = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "CALGreen Compliant",
      description: "Generate professional waste management plans that meet California's strict building code requirements."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Instant Generation",
      description: "Create your CWMP in minutes, not days. Skip the consultant back-and-forth."
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Save Thousands",
      description: "Professional plans at a fraction of traditional consultant costs."
    },
    {
      icon: <Recycle className="w-8 h-8" />,
      title: "65% Diversion",
      description: "Auto-calculate material estimates and diversion rates to meet CALGreen targets."
    }
  ];

  // Calculate savings for bundle
  const bundleSavings = (pricing.cwmp_price + pricing.final_report_price) - pricing.bundle_price;

  const plans = [
    {
      name: "CWMP Report",
      price: `$${pricing.cwmp_price}`,
      description: "Pre-construction waste management plan",
      features: [
        "CALGreen compliant document",
        "Detailed waste calculations",
        "Material breakdown tables",
        "Jurisdiction-specific notes",
        "Facility & hauler suggestions",
        "Professional PDF export"
      ],
      popular: false,
      badge: "First one FREE!"
    },
    {
      name: "Bundle",
      price: `$${pricing.bundle_price}`,
      description: "CWMP + Final Report together",
      features: [
        "Everything in CWMP Report",
        "Final Verification Report",
        "Receipt upload & tracking",
        "Variance analysis",
        "Complete compliance package",
        `Save $${bundleSavings}`
      ],
      popular: true,
      badge: "Best Value"
    },
    {
      name: "Subscription",
      price: `$${pricing.subscription_price}`,
      priceNote: "/month",
      description: "Unlimited reports for high-volume users",
      features: [
        "Unlimited CWMP reports",
        "Unlimited Final Reports",
        "All add-on features included",
        "Priority support",
        "Multi-project dashboard",
        "Cancel anytime"
      ],
      popular: false,
      badge: "Unlimited"
    }
  ];

  const steps = [
    { 
      step: 1, 
      title: "Enter Project Details", 
      description: "Address, permit number, building type, and square footage. Takes about 2-3 minutes.",
      tips: ["Have your permit application handy", "Know your total square footage"]
    },
    { 
      step: 2, 
      title: "Review Estimates", 
      description: "Auto-calculated waste estimates based on your building type and CALGreen standards.",
      tips: ["Estimates are based on industry averages", "You can adjust if needed"]
    },
    { 
      step: 3, 
      title: "Download Your Plan", 
      description: "Get your council-ready PDF instantly. Submit with your permit application.",
      tips: ["PDF is ready for building department", "Keep a copy for your records"]
    }
  ];

  // What You'll Need checklist
  const beforeYouStart = [
    { item: "Permit application or number", icon: "📋" },
    { item: "Project address", icon: "📍" },
    { item: "Building type (residential, commercial, etc.)", icon: "🏗️" },
    { item: "Total square footage", icon: "📐" },
    { item: "Owner/contractor contact details", icon: "👤" }
  ];

  // FAQ Data
  const faqs = [
    {
      question: "What is CalGreen 5.408?",
      answer: "CalGreen Section 5.408 requires California construction projects to divert at least 65% of waste from landfills. This applies to most commercial projects and residential projects over 1,000 sq ft (varies by jurisdiction)."
    },
    {
      question: "Which projects need a waste management plan?",
      answer: "New commercial construction, major renovations, demolitions, and residential projects over 1,000 sq ft typically require a CWMP. Requirements vary by county—always verify with your local building department."
    },
    {
      question: "How do I achieve 65% diversion?",
      answer: "Focus on high-diversion materials: concrete (95%+), metals (95%+), asphalt (95%+), and clean wood (70-85%). These typically make up 70-80% of construction waste by weight."
    },
    {
      question: "What documentation do I need?",
      answer: "You'll need a waste management plan, weight tickets from facilities, material quantities, and calculations proving 65%+ diversion. Our plans include all required sections."
    },
    {
      question: "Do requirements vary by county?",
      answer: "Yes. California's 58 counties may have stricter requirements or different thresholds. Our plans include jurisdiction-specific notes for your county."
    },
    {
      question: "What happens if I don't comply?",
      answer: "Non-compliance can result in permit delays, withheld certificates of occupancy, fines, and required corrective action plans."
    }
  ];

  // Material highlights for quick reference
  const materialHighlights = [
    { name: "Concrete & Masonry", rate: "95%+", tip: "Keep separate from wood and trash. Most facilities accept rebar." },
    { name: "Metals", rate: "95-98%", tip: "Separate ferrous from non-ferrous. You typically get paid for scrap." },
    { name: "Asphalt", rate: "95%+", tip: "Don't mix with concrete—different processing and markets." },
    { name: "Clean Wood", rate: "70-85%", tip: "Must be unpainted and untreated. Treated wood is hazardous waste." },
    { name: "Cardboard", rate: "70%", tip: "Keep dry. Most haulers include recycling at no extra cost." },
    { name: "Drywall", rate: "2-10%", tip: "Limited infrastructure in CA. Don't count on it for diversion targets." }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <CaliforniaFlag className="w-10 h-10" />
              <span className="font-bold text-xl text-foreground font-['Manrope']">Plan Generator</span>
              <span className="text-xl text-primary font-matrix ml-1">CA</span>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <a href="#features" className="text-foreground hover:text-primary transition-colors px-2">Features</a>
              <a href="#pricing" className="text-foreground hover:text-primary transition-colors px-2">Pricing</a>
              <a href="#calgreen" className="text-foreground hover:text-primary transition-colors px-2">CalGreen</a>
              <a href="#faq" className="text-foreground hover:text-primary transition-colors px-2">FAQ</a>
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-primary/30">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                  className="text-foreground border-primary/50 hover:bg-primary/10 px-4"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  data-testid="get-started-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 border border-primary px-4"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Beach Sunset Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1766163460377-f57d0870622e?w=1920&q=80')",
            opacity: 0.24
          }}
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/90" />
        
        <div className="relative max-w-7xl mx-auto z-10 px-4">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
            {/* Left side - Plan type cards */}
            <div className="hidden lg:flex flex-col gap-4 w-72 flex-shrink-0">
              {/* CWMP Card */}
              <div className="p-4 rounded-lg border border-primary/30 bg-card/40 backdrop-blur-sm hover:border-primary/60 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <HardHat className="w-5 h-5 text-primary" />
                  <h4 className="font-semibold text-sm text-foreground">CWMP</h4>
                </div>
                <p className="text-xs text-foreground mb-2">Construction Waste Management Plan</p>
                <ul className="space-y-1">
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Pre-construction submission
                  </li>
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Auto-calculated estimates
                  </li>
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    65% diversion compliance
                  </li>
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Jurisdiction-specific
                  </li>
                </ul>
              </div>
              
              {/* Final Report Card */}
              <div className="p-4 rounded-lg border border-primary/30 bg-card/40 backdrop-blur-sm hover:border-primary/60 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h4 className="font-semibold text-sm text-foreground">Final Report</h4>
                </div>
                <p className="text-xs text-foreground mb-2">Waste Verification Report</p>
                <ul className="space-y-1">
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Post-construction verification
                  </li>
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Actual vs estimated
                  </li>
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Receipt & manifest tracking
                  </li>
                  <li className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-primary" />
                    Compliance certification
                  </li>
                </ul>
              </div>
            </div>

            {/* Center - Main hero content */}
            <div className="flex-1 text-center animate-fade-in-up max-w-2xl">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 font-['Manrope'] tracking-tight">
                California Waste Plans<br />
                <span className="text-primary text-glow font-matrix-display">made simple</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Generate professional Construction Waste Management Plans for California projects in minutes. 
                Council-ready, county compliant PDFs that meet <span className="font-matrix text-primary">CALGreen 65%</span> diversion requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-primary/20 transition-all"
                >
                  Create Your Plan <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                  className="border-primary text-primary hover:bg-primary/10 px-8 py-6 text-lg"
                >
                  Learn More
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="mt-16 flex flex-wrap justify-center gap-8 animate-fade-in-up animate-delay-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-medium">CALGreen Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-medium">All 58 California Counties</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-medium">Instant PDF Download</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom divider line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-20 px-4 bg-card/30">
        {/* Top divider line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              Why Choose Plan Generator?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Professional waste management plans without the consultant fees or lengthy wait times.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="gradient-card border-primary/20 hover:border-primary/40 transition-all duration-300 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <CardHeader>
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4 border border-primary/30">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl text-foreground">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Bottom divider line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-20 px-4">
        {/* Top divider line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              How It Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Get your CALGreen-compliant waste management plan in three simple steps. Most users complete it in under 10 minutes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {steps.map((item, index) => (
              <div 
                key={index} 
                className="text-center relative animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`step-${item.step}`}
              >
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mx-auto mb-6 border-2 border-primary shadow-lg shadow-primary/30">
                  {item.step}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-primary/50 to-primary/10" />
                )}
                <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                <p className="text-muted-foreground mb-4">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Before You Start Checklist */}
          <div className="max-w-2xl mx-auto">
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl text-foreground flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Before You Start - What You'll Need
                </CardTitle>
                <CardDescription>
                  Have these ready to complete your plan quickly
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {beforeYouStart.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-primary/10"
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-sm text-foreground">{item.item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-center">
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="start-now-btn"
                  >
                    Start Your Plan Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Bottom divider line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-20 px-4 bg-card/30">
        {/* Top divider line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              Simple, Transparent Pricing
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Pay per report or subscribe for unlimited. First CWMP is free!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={index}
                className={`relative ${plan.popular ? 'border-primary shadow-lg glow' : 'border-primary/20'} transition-all duration-300 card-hover`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(' ', '-')}`}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${plan.popular ? 'bg-primary' : 'bg-green-500'} text-white px-4 py-1 rounded-full text-sm font-semibold`}>
                    {plan.badge}
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl text-foreground">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-primary">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.priceNote || ''}</span>
                  </div>
                  <CardDescription className="mt-2">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
                    onClick={() => navigate('/register')}
                    data-testid={`pricing-cta-${plan.name.toLowerCase().replace(' ', '-')}`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Final Report standalone note */}
          <div className="text-center mt-8">
            <p className="text-muted-foreground">
              Need just a Final Report? <span className="text-primary font-semibold">${pricing.final_report_price}</span> per report
            </p>
          </div>
        </div>
        
        {/* Bottom divider line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </section>

      {/* Plan Types */}
      <section className="relative py-20 px-4">
        {/* Top divider line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              Our Plans
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Professional waste management plans tailored to California requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="gradient-card border-primary/20 overflow-hidden" data-testid="cwmp-card">
              <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary flex items-center justify-center border-b border-primary/20">
                <HardHat className="w-20 h-20 text-primary" />
              </div>
              <CardHeader>
                <CardTitle className="text-xl text-foreground">Construction Waste Management Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Pre-construction submission document
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Auto-calculated material estimates
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    65% diversion compliance
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Jurisdiction-specific requirements
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="gradient-card border-primary/20 overflow-hidden" data-testid="final-report-card">
              <div className="h-48 bg-gradient-to-br from-secondary to-primary/20 flex items-center justify-center border-b border-primary/20">
                <Building2 className="w-20 h-20 text-primary" />
              </div>
              <CardHeader>
                <CardTitle className="text-xl text-foreground">Final Verification Report</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Post-construction verification
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Actual vs estimated comparison
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Receipt & manifest tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Compliance certification
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Bottom divider line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </section>

      {/* CalGreen 5.408 Overview Section */}
      <section id="calgreen" className="relative py-20 px-4 bg-card/30">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              CalGreen 5.408 Compliance
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              California requires 65% waste diversion on construction projects. Here is what you need to know.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="border-primary/20 bg-card/50">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Scale className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-foreground">The Requirement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Divert at least <span className="text-primary font-semibold">65% by weight</span> of construction and demolition debris from landfills through recycling, reuse, or salvage.
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-card/50">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-foreground">Who Must Comply</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Commercial construction & renovations</li>
                  <li>• Residential projects over 1,000 sq ft</li>
                  <li>• Demolition projects</li>
                  <li>• Mixed-use developments</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-card/50">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-500" />
                </div>
                <CardTitle className="text-lg text-foreground">County Variations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Each of California&apos;s <span className="text-primary font-semibold">58 counties</span> may have stricter requirements. Some require online portal submissions. Always verify locally.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-card/50 border border-primary/20 rounded-lg p-6 max-w-3xl mx-auto">
            <h3 className="font-semibold text-foreground mb-3">Required Documentation</h3>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                Waste management plan
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                Weight tickets from facilities
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                Material quantities by type
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                65%+ diversion calculations
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </section>

      {/* Materials Quick Guide */}
      <section id="materials" className="relative py-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              Material Diversion Rates
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Focus on high-diversion materials to hit your 65% target. Not all materials are equal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {materialHighlights.map((material, index) => (
              <div 
                key={index}
                className="p-4 rounded-lg border border-primary/20 bg-card/50 hover:border-primary/40 transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-foreground">{material.name}</h4>
                  <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                    material.rate.includes('95') ? 'bg-green-500/20 text-green-400' :
                    material.rate.includes('70') ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {material.rate}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{material.tip}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              <span className="text-primary font-semibold">Pro tip:</span> Concrete, metals, asphalt, and clean wood typically represent 70-80% of C&D waste. 
              Diverting just these four materials can exceed 65% compliance.
            </p>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-20 px-4 bg-card/30">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">
              Quick answers about CalGreen compliance and waste management plans.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-primary/20 rounded-lg overflow-hidden bg-card/50"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-primary/5 transition-colors"
                  data-testid={`faq-${index}`}
                >
                  <span className="font-medium text-foreground pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-primary flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-muted-foreground">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 bg-card/50">
        {/* Top divider line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 font-['Manrope']">
            Ready to Get Started?
          </h2>
          <p className="text-muted-foreground mb-8">
            Create your CALGreen-compliant waste management plan today.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            data-testid="final-cta-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg font-semibold"
          >
            Create Free Account <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-primary/20 bg-card/20">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CaliforniaFlag className="w-8 h-8" />
                <span className="font-bold text-foreground">Plan Generator</span>
                <span className="text-primary font-matrix">CA</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-xs mb-2">
                Professional CalGreen-compliant waste management plans for all 58 California counties.
              </p>
              <p className="text-xs text-muted-foreground">
                <a href="https://www.plangeneratorca.com" className="hover:text-primary transition-colors">www.plangeneratorca.com</a>
                <span className="mx-2">•</span>
                <a href="mailto:nate@plangeneratorca.com" className="hover:text-primary transition-colors">nate@plangeneratorca.com</a>
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
              <div>
                <h4 className="font-semibold text-foreground mb-3">Product</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                  <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                  <li><a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-3">Resources</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#calgreen" className="hover:text-foreground transition-colors">CalGreen Guide</a></li>
                  <li><a href="#materials" className="hover:text-foreground transition-colors">Materials Guide</a></li>
                  <li><a href="#faq" className="hover:text-foreground transition-colors">FAQ</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-3">Legal</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#disclaimer" className="hover:text-foreground transition-colors">Disclaimer</a></li>
                  <li><a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a></li>
                  <li><a href="#" className="hover:text-foreground transition-colors">Terms of Service</a></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div id="disclaimer" className="border-t border-primary/20 pt-6 mb-6">
            <div className="bg-card/50 border border-primary/10 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Important Disclaimer
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Plan Generator is a planning tool, not a compliance certification service. All plans must be reviewed by qualified professionals 
                and verified with your local jurisdiction before submission. We are not affiliated with any California state agency, CalRecycle, 
                or local building departments. Requirements vary by county—always confirm with your local building department. Information provided 
                is for general guidance only and does not constitute legal, engineering, or professional advice. Use of this service does not 
                guarantee regulatory compliance or plan acceptance by any jurisdiction.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground border-t border-primary/10 pt-6">
            <p>© 2026 Plan Generator CA. All rights reserved.</p>
            <p className="text-xs">Serving all 58 California counties</p>
          </div>
        </div>
      </footer>

      {/* Floating Contact Support Button */}
      <div className="fixed bottom-20 right-6 z-40">
        <Button
          onClick={() => setShowContactModal(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-full px-4 py-6"
          data-testid="contact-support-btn"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Support
        </Button>
      </div>

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
    </div>
  );
};

export default LandingPage;
